// usePortraitAudio — audio + hover-controls state for the View-Wheel center
// portrait video player (PortraitVideoOverlay). Extracted so the overlay
// component stays under the complexity threshold (Nick #4: retrofit as we go).
//
// Owns: default-MUTED audio (autoplay-safe, never auto-unmuted), a 0..100
// volume mapped to <video>.volume, and the hover/focus REVEAL of the controls.
// The consumer wires `applyAudioToVideo` into the <video> ref callback so a
// (re)mounted node picks up the current muted/volume from state.
//
// CONTROLS ARE ALWAYS MOUNTED while a video is showing — they are never
// unmounted on mouse-leave; at rest they are hidden (opacity 0 + pointer-events
// none + aria-hidden). Unmounting them raced React's cleanup against the D3
// WheelComponent parent that shares the same container, throwing
// "Failed to execute 'removeChild' on 'Node'" on rapid hover in/out
// (Daniyal QA #3651). Keeping them mounted removes the race entirely.
import { useCallback, useEffect, useRef, useState } from "react";

export default function usePortraitAudio(videoRef) {
  // DEFAULT MUTED so autoplay is always allowed. Refs mirror state so the ref
  // callback can read the latest value synchronously on (re)mount.
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(100); // 0..100 -> <video>.volume 0..1
  const mutedRef = useRef(true);
  const volumeRef = useRef(1);

  // Hover/focus reveal for the controls. `reveal` alone drives visibility — the
  // controls stay mounted at rest and simply fade (CSS opacity transition).
  const [reveal, setReveal] = useState(false);

  // Keep the live <video>.muted in sync — flips only on explicit user action.
  useEffect(() => {
    mutedRef.current = muted;
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted, videoRef]);

  // Keep the live <video>.volume in sync with the slider.
  useEffect(() => {
    const v = Math.min(1, Math.max(0, volume / 100));
    volumeRef.current = v;
    if (videoRef.current) videoRef.current.volume = v;
  }, [volume, videoRef]);

  // Apply the current muted/volume to a freshly-mounted <video> node. Chrome's
  // autoplay gate checks the muted ATTRIBUTE at load time (not just the IDL
  // property) — a node whose muted PROPERTY was set imperatively but never had
  // the attribute reflected sits paused on its poster forever (#927). Set both,
  // imperatively, the instant the node mounts (React reflects the attribute
  // unreliably from the property alone).
  const applyAudioToVideo = useCallback((node) => {
    if (node) {
      node.muted = mutedRef.current;
      node.volume = volumeRef.current;
      if (mutedRef.current) {
        node.setAttribute("muted", "");
      } else {
        node.removeAttribute("muted");
      }
    }
  }, []);

  const toggleMute = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      if (e.preventDefault) e.preventDefault();
    }
    setMuted((m) => {
      const next = !m;
      // Unmuting while the slider sits at 0 would be "unmuted but silent" — bump
      // the volume back to full so the toggle actually produces sound (#927i).
      if (!next && volumeRef.current === 0) setVolume(100);
      return next;
    });
  }, []);

  // #927i: the volume slider is the master audio control — setting it above 0
  // UNMUTES, dragging it to 0 MUTES (mirrors every native media player).
  const onVolume = useCallback((e) => {
    e.stopPropagation();
    const val = Number(e.target.value);
    setVolume(val);
    setMuted(val === 0);
  }, []);

  return {
    muted,
    volume,
    setReveal,
    // `controlsVisible` is the sole visibility signal now — the controls are
    // always rendered while a video shows; this only toggles their hidden/shown
    // styling (opacity, pointer-events, aria-hidden), never their mount.
    controlsVisible: reveal,
    applyAudioToVideo,
    toggleMute,
    onVolume,
  };
}
